import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useAsk } from '../context/PromptProvider'
import { useToast } from '../context/ToastContext'
import { parseDateOnly, roundBillable } from '../utils/format'
import { toFirmWallClock } from '../utils/eventTime'
import { daysUntilDue, firmDueDay, isDueAfterStartDay } from '../utils/todoDue'
// Code review of 9ff73f1, finding #9 — the icon mapping is shared with
// Calendar and To-Do; this page's own copy had drifted from theirs.
import { getEventIcon } from '../lib/eventTypes'
import { SkeletonTable, LoadingRegion } from '../components/Skeleton'
import useTimerEntrySaved from '../hooks/useTimerEntrySaved'
import useFirmTimezone from '../hooks/useFirmTimezone'
import EmptyState from '../components/EmptyState'
import HelpButton from '../components/HelpButton'

// Dates on this page are the FIRM's calendar days and clock (2026-10-17,
// James: "a task or event at 5 PM Pacific or later doesn't show as due
// today").  The server lists tasks_due / upcoming by the firm-local day
// (routes/dailies.js); these labels use the same day rule (utils/todoDue.js).
// Before, they used date-fns differenceInDays on `new Date(...)`: elapsed
// 24-hour periods in the BROWSER's zone, with a DATE column parsed as UTC
// midnight — so a task due today read "Overdue by 1 day" from 5 PM Pacific.
//
// An Amicus to-do stored from the day work starts to the day it is due is
// listed, labelled and counted by its DUE day, on the server and here (James
// 2026-10-21, "due date everywhere"): the server lists it under Tasks & Events
// Due only from that day, "Mark All Overdue Done" closes it only after it, and
// its row shows no start time (that time is on another day).

// Format a Date, or '' when it is missing or invalid (a bad row must not
// take the page down).
const fmt = (d, pattern) => (d instanceof Date && !isNaN(d.getTime()) ? format(d, pattern) : '')
// An instant's time of day on the firm's clock, never the browser's.
const firmTimeOfDay = (value) => fmt(toFirmWallClock(value), 'h:mm a')

const getPriorityInfo = (event) => {
  if (event.is_completed) return { label: 'Completed', color: 'bg-gray-100 text-gray-500', days: null }
  const days = daysUntilDue(event)
  if (days === null) return { label: 'No date', color: 'bg-gray-100 text-gray-500', days: null }
  if (days < 0) return { label: `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`, color: 'bg-gray-900 text-white', days }
  if (days === 0) return { label: 'Due today!', color: 'bg-red-600 text-white', days }
  if (days === 1) return { label: 'Due tomorrow', color: 'bg-red-100 text-red-700', days }
  if (days <= 2) return { label: `Due in ${days} days`, color: 'bg-red-100 text-red-700', days }
  if (days <= 6) return { label: `Due in ${days} days`, color: 'bg-orange-100 text-orange-700', days }
  if (days <= 13) return { label: `Due in ${days} days`, color: 'bg-yellow-100 text-yellow-700', days }
  return { label: `Due in ${days} days`, color: 'bg-green-100 text-green-700', days }
}

const commIcon = (type) => {
  const icons = { phone_call: '📞', email: '📧', letter: '✉️', text: '💬', in_person: '🤝', video_call: '📹', fax: '📠' }
  return icons[type] || '📝'
}

export default function Dailies() {
  const { user } = useAuth()
  // The firm zone for every date and time below — fetched when this page is
  // a pop-out (PopupLayout never sets it), re-rendering once it is known.
  useFirmTimezone()
  const ask = useAsk()
  const toast = useToast()
  const isAdmin = user?.role === 'admin' || user?.permissions?.is_admin === true
  const canViewAllTime = isAdmin || user?.permissions?.can_view_all_time === true
  // "My time only / View all" toggle — only meaningful for users who normally see firm-wide.
  // Persisted in localStorage so the choice survives page refresh.
  const [scopeMine, setScopeMine] = useState(() => {
    try { return localStorage.getItem('lextrack_dailies_scope') === 'mine' } catch { return false }
  })
  const [data, setData] = useState(null)
  const [actionError, setActionError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchDailies() }, [scopeMine])
  useTimerEntrySaved(() => fetchDailies())

  // The server caches this snapshot for up to 2 minutes, and (until the API
  // invalidates it on every calendar / time-entry write) a refetch right
  // after Done or Post can return the item again.  Items this page just
  // completed or posted stay hidden for 3 minutes, longer than that cache.
  const handledRef = useRef({ events: new Map(), entries: new Map() })
  const HANDLED_MS = 3 * 60 * 1000
  const withoutHandled = (payload) => {
    if (!payload) return payload
    const now = Date.now()
    const live = (m, id) => { const t = m.get(String(id)); if (t && now - t < HANDLED_MS) return true; if (t) m.delete(String(id)); return false }
    const { events, entries } = handledRef.current
    return {
      ...payload,
      tasks_due: Array.isArray(payload.tasks_due) ? payload.tasks_due.filter(e => !live(events, e.id)) : payload.tasks_due,
      upcoming_events: Array.isArray(payload.upcoming_events) ? payload.upcoming_events.filter(e => !live(events, e.id)) : payload.upcoming_events,
      unposted_entries: Array.isArray(payload.unposted_entries) ? payload.unposted_entries.filter(e => !live(entries, e.id)) : payload.unposted_entries,
    }
  }

  const fetchDailies = async () => {
    try {
      const url = scopeMine && canViewAllTime ? '/api/dailies?scope=mine' : '/api/dailies'
      const res = await api.get(url)
      setData(withoutHandled(res.data))
    } catch (err) {
      setActionError(err.response?.data?.error || 'Error loading daily data')
    } finally {
      setLoading(false)
    }
  }

  const toggleScope = () => {
    const next = !scopeMine
    setScopeMine(next)
    try { localStorage.setItem('lextrack_dailies_scope', next ? 'mine' : 'all') } catch {}
  }

  const handleComplete = async (eventId) => {
    try {
      await api.put(`/api/calendar/${eventId}/complete`, { completion_note: '' })
      setActionError('')
      handledRef.current.events.set(String(eventId), Date.now())
      setData(d => withoutHandled(d))
      fetchDailies()
    } catch (err) {
      setActionError(err.response?.data?.error || 'Error completing event')
    }
  }

  const handlePost = async (entryId) => {
    try {
      await api.put(`/api/time-entries/${entryId}/post`)
      setActionError('')
      handledRef.current.entries.set(String(entryId), Date.now())
      setData(d => withoutHandled(d))
      fetchDailies()
    } catch (err) {
      setActionError(err.response?.data?.error || 'Error posting entry')
    }
  }

  if (loading) return (
    <LoadingRegion label="Loading daily snapshot">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
          <SkeletonTable rows={3} cols={3} />
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
          <SkeletonTable rows={3} cols={3} />
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <SkeletonTable rows={5} cols={4} />
      </div>
    </LoadingRegion>
  )

  if (!data) return (
    <EmptyState
      variant="error"
      icon="⚠️"
      title="Unable to load dailies data"
      description="The daily snapshot couldn't be loaded. Try refreshing the page."
    />
  )

  const { time_summary, tasks_due, upcoming_events, unposted_entries, recent_communications, urgent_sol, viewing_all } = data

  return (
    <div>
      {actionError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{actionError}</div>}
      {/* Wraps in a narrow pop-out: the buttons drop below the title
          instead of squeezing "View All Time" onto two lines. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-testid="dailies-header">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-800">Daily Snapshot<HelpButton slug="dailies" /></h2>
          <p className="text-sm text-gray-500 mt-1">
            {fmt(toFirmWallClock(new Date()), 'EEEE, MMMM d, yyyy')}
            {viewing_all && ' — Viewing all attorneys'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canViewAllTime && (
            <button onClick={toggleScope}
              title={scopeMine ? 'Showing only your data — click to view firm-wide' : 'Showing firm-wide — click to view your data only'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                scopeMine
                  ? 'bg-purple-600 border-purple-600 text-white hover:bg-purple-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}>
              {scopeMine ? '👤 My Time Only' : '👥 View All Time'}
            </button>
          )}
          <button onClick={fetchDailies}
            className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            Refresh
          </button>
        </div>
      </div>

      {/* Today's Time Summary (only when caller has time_entries perm) */}
      {time_summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Today's Hours</p>
            <p className="text-2xl font-bold text-gray-800">{parseFloat(time_summary.total_hours).toFixed(1)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Unposted Today</p>
            <p className="text-2xl font-bold text-yellow-600">{parseFloat(time_summary.unposted_hours).toFixed(1)} hrs</p>
            <p className="text-xs text-gray-400">${parseFloat(time_summary.unposted_amount).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Posted Today</p>
            <p className="text-2xl font-bold text-green-600">{parseFloat(time_summary.posted_hours).toFixed(1)} hrs</p>
            <p className="text-xs text-gray-400">${parseFloat(time_summary.posted_amount).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-center">
            <Link to="/time-entries" className="text-sm text-primary-600 hover:text-primary-800 font-medium hover:underline">
              View All Time Entries →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tasks & Events Due Today / Overdue (only when caller has tasks or calendar perm) */}
        {tasks_due && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-800">
              Tasks & Events Due
              <span className="ml-2 text-sm text-gray-400">({tasks_due.length})</span>
            </h3>
            {tasks_due.some(e => (daysUntilDue(e) ?? 0) < 0) && (
              <button onClick={async () => {
                if (!await ask({ type: 'confirm', title: 'Mark all overdue as done?', message: 'Mark all overdue items as done?', confirmLabel: 'Mark done' })) return
                try {
                  const res = await api.put('/api/calendar/complete-overdue')
                  const n = res?.data?.completed ?? 0
                  setActionError('')
                  await fetchDailies()
                  // Tell the user what actually happened — silent success felt
                  // like the button was broken when the cache hid the change.
                  if (n > 0) toast.success(`Marked ${n} overdue item${n === 1 ? '' : 's'} done.`)
                  else toast.info('Nothing to do — no overdue items left.')
                } catch (err) {
                  setActionError(err.response?.data?.error || 'Error completing overdue items')
                }
              }}
                className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                ✓ Mark All Overdue Done
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-[calc(400px*var(--app-zoom,1))] overflow-y-auto">
            {tasks_due.length === 0 ? (
              <EmptyState
                icon="🎉"
                title="You're caught up!"
                description="Nothing overdue or due today."
                className="py-8"
              />
            ) : (
              tasks_due.map(event => {
                const priority = getPriorityInfo(event)
                return (
                  <div key={event.id} className="px-6 py-3 hover:bg-gray-50">
                    {/* min-w-0 lets the text column shrink to the card, so a
                        long title wraps instead of widening the row past
                        the list's edge (and ✓ Done with it). */}
                    <div className="flex items-start justify-between gap-3" data-testid="dailies-due-row">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span>{getEventIcon(event.event_type)}</span>
                          <p className="text-sm font-medium text-gray-800 min-w-0 break-words">{event.title}</p>
                        </div>
                        {event.matter_title && (
                          <p className="text-xs text-primary-600 ml-6 break-words">📁 {event.matter_title}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1 ml-6">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}>
                            {priority.label}
                          </span>
                          {event.start_time && !isDueAfterStartDay(event) && (
                            <span className="text-xs text-gray-400">
                              {firmTimeOfDay(event.start_time)}
                            </span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => handleComplete(event.id)}
                        className="shrink-0 whitespace-nowrap text-xs text-green-600 hover:text-green-800 border border-green-200 px-2 py-1 rounded hover:bg-green-50 transition-colors">
                        ✓ Done
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        )}

        {/* Upcoming Events (next 7 days, only when caller has calendar perm) */}
        {upcoming_events && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">
              Upcoming (Next 7 Days)
              <span className="ml-2 text-sm text-gray-400">({upcoming_events.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[calc(400px*var(--app-zoom,1))] overflow-y-auto">
            {upcoming_events.length === 0 ? (
              <EmptyState
                icon="📅"
                title="No upcoming events this week"
                description="Schedule hearings, deadlines, and meetings from the calendar."
                action={{ label: '+ Add Event', href: '/calendar?new=1' }}
                className="py-8"
              />
            ) : (
              upcoming_events.map(event => {
                const priority = getPriorityInfo(event)
                return (
                  <div key={event.id} className={`px-6 py-3 hover:bg-gray-50 ${event.is_completed ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between gap-3" data-testid="dailies-upcoming-row">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span>{getEventIcon(event.event_type)}</span>
                          <p className="text-sm font-medium text-gray-800 min-w-0 break-words">{event.title}</p>
                        </div>
                        {event.matter_title && (
                          <p className="text-xs text-primary-600 ml-6 break-words">📁 {event.matter_title}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1 ml-6">
                          <span className="text-xs text-gray-500">
                            {fmt(firmDueDay(event), 'EEE, MMM d')}
                            {event.start_time && !isDueAfterStartDay(event) && ` at ${firmTimeOfDay(event.start_time)}`}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}>
                            {priority.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        )}

        {/* Unposted Time Entries (only when caller has time_entries perm) */}
        {unposted_entries && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">
              Unposted Time
              <span className="ml-2 text-sm text-gray-400">({unposted_entries.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[calc(400px*var(--app-zoom,1))] overflow-y-auto">
            {unposted_entries.length === 0 ? (
              <EmptyState
                icon="✅"
                title="All time entries are posted!"
                description="Every entry has been reviewed and posted for billing."
                className="py-8"
              />
            ) : (
              unposted_entries.map(entry => (
                <div key={entry.id} className="px-6 py-3 hover:bg-gray-50">
                  {/* Without min-w-0 the truncate lines never truncate: each
                      row grew to the full description (~1900 px) inside the
                      scrolling list and Post sat off its visible edge. */}
                  <div className="flex items-start justify-between gap-3" data-testid="dailies-unposted-row">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate" title={entry.matter_title || undefined}>
                        {entry.matter_title || 'No matter'}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5" title={entry.description || undefined}>{entry.description || 'No description'}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                        <span className="text-xs text-gray-400">{format(parseDateOnly(entry.date), 'MMM d')}</span>
                        <span className="text-xs text-gray-400">{entry.attorney_name}</span>
                        <span className="text-xs font-medium text-gray-600">{roundBillable(entry.hours).toFixed(1)} hrs</span>
                        <span className="text-xs text-gray-400">${(roundBillable(entry.hours) * parseFloat(entry.rate)).toFixed(2)}</span>
                      </div>
                    </div>
                    <button onClick={() => handlePost(entry.id)}
                      className="shrink-0 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-2 py-1 rounded hover:bg-blue-50 transition-colors whitespace-nowrap">
                      Post
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Urgent SOL */}
        {urgent_sol && urgent_sol.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-red-100 bg-red-50">
              <h3 className="font-semibold text-red-800">
                SOL Deadlines (90 days)
                <span className="ml-2 text-sm text-red-500">({urgent_sol.length})</span>
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[calc(300px*var(--app-zoom,1))] overflow-y-auto">
              {urgent_sol.map(entry => {
                // sol_date is a DATE: firm calendar days, same rule as above.
                const days = daysUntilDue({ due_date: entry.sol_date })
                const color = days !== null && days < 0 ? 'bg-gray-900 text-white' : days !== null && days <= 30 ? 'bg-red-600 text-white' : 'bg-orange-100 text-orange-700'
                const label = days === null ? 'No date' : days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days} days`
                return (
                  <div key={entry.id} className="px-6 py-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span>⚖️</span>
                          <p className="text-sm font-medium text-gray-800">{entry.cause_of_action}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-6">
                          {entry.matter_title} — {entry.first_name} {entry.last_name} — SOL: {fmt(parseDateOnly(entry.sol_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Recent Communications (only when caller has communications perm) */}
        {recent_communications && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Recent Communications</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recent_communications.length === 0 ? (
              <EmptyState
                icon="📞"
                title="No communications logged yet"
                description="Log phone calls, emails, and meetings to build a matter timeline."
                action={{ label: 'Log Communication', href: '/communications' }}
                className="py-8"
              />
            ) : (
              recent_communications.map(comm => (
                <div key={comm.id} className="px-6 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-2">
                    <span>{commIcon(comm.comm_type)}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      comm.direction === 'incoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {comm.direction === 'incoming' ? '← In' : '→ Out'}
                    </span>
                    <p className="text-sm font-medium text-gray-800 truncate flex-1">{comm.subject}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-1 ml-7">
                    {comm.matter_title && (
                      <span className="text-xs text-primary-600">📁 {comm.matter_title}</span>
                    )}
                    <span className="text-xs text-gray-400">
                      {format(toFirmWallClock(comm.comm_date), 'MMM d, h:mm a')}
                    </span>
                    <span className="text-xs text-gray-400">{comm.logged_by_name}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
